import { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Stack,
} from '@mui/material'
import {
  useFeatureAnnouncements,
} from '../../../contexts/FeatureAnnouncementContext'
import {
  createCommandPaletteAnnouncement,
  createFeatureAnnouncement,
  createBadgeAnnouncement,
  createInlineAnnouncement,
} from '../../../utils/featureAnnouncements'
import {
  FeatureAnnouncementInline,
  clearDismissedStatus,
} from '../../../components/FeatureAnnouncement'

export default function FeatureAnnouncementTest() {
  const { addAnnouncement, removeAnnouncement, clearAll } = useFeatureAnnouncements()
  const [testResults, setTestResults] = useState<string[]>([])

  const addTestResult = (message: string) => {
    setTestResults((prev) => [...prev, `${new Date().toLocaleTimeString()}: ${message}`])
  }

  // Test 1: Command Palette Banner
  const testCommandPalette = () => {
    clearDismissedStatus('command-palette-intro')
    addAnnouncement(createCommandPaletteAnnouncement())
    addTestResult('✅ Command Palette banner added')
  }

  // Test 2: Success Banner
  const testSuccessBanner = () => {
    clearDismissedStatus('test-success-banner')
    addAnnouncement(
      createFeatureAnnouncement(
        'test-success-banner',
        'Success!',
        'This is a success message banner. You can dismiss it.',
        {
          color: 'success',
          actionLabel: 'Learn More',
          onAction: () => {
            addTestResult('✅ Success banner action clicked')
          },
        }
      )
    )
    addTestResult('✅ Success banner added')
  }

  // Test 3: Warning Banner
  const testWarningBanner = () => {
    clearDismissedStatus('test-warning-banner')
    addAnnouncement(
      createFeatureAnnouncement(
        'test-warning-banner',
        'Warning',
        'This is a warning message. Please review your settings.',
        {
          color: 'warning',
          persistent: true, // This will show every time until dismissed
        }
      )
    )
    addTestResult('✅ Warning banner added (persistent)')
  }

  // Test 4: Error Banner
  const testErrorBanner = () => {
    clearDismissedStatus('test-error-banner')
    addAnnouncement(
      createFeatureAnnouncement(
        'test-error-banner',
        'Error',
        'Something went wrong. Please try again.',
        {
          color: 'error',
        }
      )
    )
    addTestResult('✅ Error banner added')
  }

  // Test 5: Info Banner
  const testInfoBanner = () => {
    clearDismissedStatus('test-info-banner')
    addAnnouncement(
      createFeatureAnnouncement(
        'test-info-banner',
        'New Feature Available',
        'Check out our latest update with improved performance.',
        {
          color: 'info',
          actionLabel: 'Try It',
          onAction: () => {
            addTestResult('✅ Info banner action clicked')
          },
        }
      )
    )
    addTestResult('✅ Info banner added')
  }

  // Test 6: Multiple Banners
  const testMultipleBanners = () => {
    clearDismissedStatus('test-multiple-1')
    clearDismissedStatus('test-multiple-2')
    clearDismissedStatus('test-multiple-3')
    
    addAnnouncement(
      createFeatureAnnouncement(
        'test-multiple-1',
        'First Banner',
        'This is the first banner',
        { color: 'info' }
      )
    )
    addAnnouncement(
      createFeatureAnnouncement(
        'test-multiple-2',
        'Second Banner',
        'This is the second banner',
        { color: 'success' }
      )
    )
    addAnnouncement(
      createFeatureAnnouncement(
        'test-multiple-3',
        'Third Banner',
        'This is the third banner',
        { color: 'warning' }
      )
    )
    addTestResult('✅ Multiple banners added (3 banners)')
  }

  // Test 7: Clear All
  const testClearAll = () => {
    clearAll()
    addTestResult('✅ All announcements cleared')
  }

  // Test 8: Reset Dismissed Status
  const testResetDismissed = () => {
    // Clear all common test IDs
    const testIds = [
      'command-palette-intro',
      'test-success-banner',
      'test-warning-banner',
      'test-error-banner',
      'test-info-banner',
      'test-multiple-1',
      'test-multiple-2',
      'test-multiple-3',
    ]
    testIds.forEach((id) => clearDismissedStatus(id))
    addTestResult('✅ All dismissed statuses reset')
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Feature Announcement System - Test Page
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Use this page to test all announcement types and features. Announcements will appear at the
        top of the page.
      </Typography>

      <Grid container spacing={3}>
        {/* Test Controls */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Banner Tests
              </Typography>
              <Stack spacing={2}>
                <Button variant="contained" onClick={testCommandPalette} fullWidth>
                  Test Command Palette Banner
                </Button>
                <Button variant="contained" color="success" onClick={testSuccessBanner} fullWidth>
                  Test Success Banner
                </Button>
                <Button variant="contained" color="warning" onClick={testWarningBanner} fullWidth>
                  Test Warning Banner (Persistent)
                </Button>
                <Button variant="contained" color="error" onClick={testErrorBanner} fullWidth>
                  Test Error Banner
                </Button>
                <Button variant="contained" color="info" onClick={testInfoBanner} fullWidth>
                  Test Info Banner
                </Button>
                <Divider />
                <Button variant="outlined" onClick={testMultipleBanners} fullWidth>
                  Test Multiple Banners
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Utility Tests
              </Typography>
              <Stack spacing={2}>
                <Button variant="outlined" color="error" onClick={testClearAll} fullWidth>
                  Clear All Announcements
                </Button>
                <Button variant="outlined" onClick={testResetDismissed} fullWidth>
                  Reset Dismissed Status
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Inline Announcement Test */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Inline Announcement Test
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This is an inline announcement that appears within the content area:
              </Typography>
              <FeatureAnnouncementInline
                announcement={createInlineAnnouncement(
                  'test-inline-1',
                  'This is an inline announcement. It appears within the content area and can be dismissed.',
                  {
                    color: 'info',
                    dismissible: true,
                  }
                )}
                onDismiss={(id) => {
                  addTestResult(`✅ Inline announcement dismissed: ${id}`)
                }}
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  You can add more inline announcements programmatically:
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() => {
                    clearDismissedStatus('test-inline-2')
                    addAnnouncement(
                      createInlineAnnouncement(
                        'test-inline-2',
                        'Another inline announcement with success color',
                        {
                          color: 'success',
                          dismissible: true,
                        }
                      )
                    )
                    addTestResult('✅ Inline announcement added')
                  }}
                >
                  Add Inline Announcement
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Test Results */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Results
              </Typography>
              <Box
                sx={{
                  maxHeight: 300,
                  overflowY: 'auto',
                  backgroundColor: 'grey.50',
                  p: 2,
                  borderRadius: 1,
                }}
              >
                {testResults.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No test results yet. Click buttons above to test.
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {testResults.map((result, index) => (
                      <Typography key={index} variant="body2" fontFamily="monospace">
                        {result}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Box>
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 2 }}
                onClick={() => setTestResults([])}
              >
                Clear Results
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Instructions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Testing Instructions
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <li>
                  <strong>Banner Tests:</strong> Click any banner test button to add an announcement
                  at the top of the page
                </li>
                <li>
                  <strong>Dismissal:</strong> Click the X button on banners to dismiss them
                </li>
                <li>
                  <strong>Persistence:</strong> Warning banner is persistent (shows every time),
                  others are one-time
                </li>
                <li>
                  <strong>Reset Dismissed:</strong> Use "Reset Dismissed Status" to show dismissed
                  announcements again
                </li>
                <li>
                  <strong>Clear All:</strong> Removes all active announcements immediately
                </li>
                <li>
                  <strong>Check localStorage:</strong> Open DevTools → Application → Local Storage
                  to see dismissed announcements stored as `feature_announcement_{id}`
                </li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  )
}

